/**
 * Admin need-mappings API layer — PIM Wave P4 Task 7. Typed calls to the
 * SHIPPED PIM Wave P4 Task 4 `/admin/catalog/need-mappings*` routes
 * (`apps/api/garden/features/catalog_schema/need_router.py`), gated
 * `marketplace.attribute.manage` (same permission as the field/group/option/
 * binding admin CRUD in `attribute-fields/api.ts` — need mappings are
 * catalog-schema configuration, not a separate permission domain). This
 * task adds NO backend routes.
 *
 * Types mirror `apps/api/garden/features/catalog_schema/schemas.py:603-645`
 * (`NeedMappingCreate`, `NeedMappingUpdate`, `NeedMappingResponse`,
 * `NeedMappingListResponse`). `weight` is a `Decimal` on the server —
 * Pydantic v2 serialises `Decimal` as a JSON STRING by default (mirrors
 * `matching_weight` in `attribute-fields/api.ts`), so the UI keeps it a
 * string to the API boundary.
 *
 * `slug` pattern — DEVIATION FROM THE BRIEF, called out explicitly (same
 * resolution `attribute-fields/lib/slug.ts` already documents for the
 * identical situation): the brief's own text calls the pattern
 * "kebab-case", but the ACTUAL backend `_SLUG_PATTERN`
 * (`catalog_schema/schemas.py:61`) is `^[a-z][a-z0-9_]*$` — snake_case,
 * NO hyphen — the exact same regex `field`/`group`/`option` slugs use.
 * `MatcherBuilder.tsx` reuses `FIELD_SLUG_PATTERN`/`FIELD_SLUG_MAX_LEN` from
 * `attribute-fields/lib/slug` rather than re-declaring the identical regex a
 * fourth time.
 *
 * `matcher` is one of two shapes (validated server-side against the target
 * field's `field_type` — the client only disables/hides the wrong inputs,
 * it never re-derives the server's own validation):
 * `{"kind":"numeric_range","min":<number|null>,"max":<number|null>}`
 * (size/number fields) or `{"kind":"option_set","value_slugs":[...]}`
 * (select fields). See `matcherKindForFieldType` below.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

export const NEED_MAPPING_MATCHER_KIND_VALUES = ["numeric_range", "option_set"] as const;
export type NeedMappingMatcherKind = (typeof NEED_MAPPING_MATCHER_KIND_VALUES)[number];

/** `{"kind":"numeric_range","min":<number|null>,"max":<number|null>}` — at
 * least one of `min`/`max` is required (server-enforced, `pim.need_mapping_
 * matcher_invalid` otherwise). Targets a `size`/`number` field. */
export interface NumericRangeMatcher {
  kind: "numeric_range";
  min: number | null;
  max: number | null;
}

/** `{"kind":"option_set","value_slugs":[...]}` — non-empty (server-enforced).
 * Targets a `select` field; every slug must exist among that field's
 * `attribute_field_options` (archived options still count as "known"). */
export interface OptionSetMatcher {
  kind: "option_set";
  value_slugs: string[];
}

export type NeedMappingMatcher = NumericRangeMatcher | OptionSetMatcher;

/** Mirrors `NeedMappingResponse`. */
export interface NeedMapping {
  id: string;
  slug: string;
  /** Per-language trigger phrases, e.g. `{ ru: [...], ky: [...], en: [...] }`.
   * Informational at this task — the chatbot's need-extraction prompt (PIM
   * Wave P4 Task 5) reads it; this admin CRUD surface never matches against
   * it. */
  phrases: Record<string, string[]>;
  field_id: string;
  matcher: NeedMappingMatcher;
  /** `Decimal` on the server, serialised as a JSON string on the wire.
   * Reserved for a future ranking-weight use — nothing reads it yet. */
  weight: string;
}

/** `GET /admin/catalog/need-mappings` (mirrors `NeedMappingListResponse`) —
 * the FULL list, un-paginated (backend doc comment: "a small, admin-curated
 * set", mirrors `FieldGroupListResponse`). */
export interface NeedMappingListResponse {
  need_mappings: NeedMapping[];
}

/** Body for `POST /admin/catalog/need-mappings` (mirrors `NeedMappingCreate`). */
export interface NeedMappingCreate {
  slug: string;
  phrases: Record<string, string[]>;
  field_id: string;
  matcher: NeedMappingMatcher;
  weight?: string;
}

/** Body for `PATCH /admin/catalog/need-mappings/{id}` (mirrors
 * `NeedMappingUpdate`, partial only — the backend DTO is `extra="forbid"`). */
export interface NeedMappingUpdate {
  slug?: string;
  phrases?: Record<string, string[]>;
  field_id?: string;
  matcher?: NeedMappingMatcher;
  weight?: string;
}

export function listNeedMappings(): Promise<NeedMappingListResponse> {
  return apiClient.get<NeedMappingListResponse>("/admin/catalog/need-mappings");
}

export function createNeedMapping(body: NeedMappingCreate): Promise<NeedMapping> {
  return apiClient.post<NeedMapping, NeedMappingCreate>("/admin/catalog/need-mappings", body);
}

export function updateNeedMapping(
  id: string,
  body: NeedMappingUpdate,
): Promise<NeedMapping> {
  // PATCH partial-update — no `apiClient.patch` shortcut (attribute-fields precedent).
  return apiFetch<NeedMapping, NeedMappingUpdate>({
    method: "PATCH",
    path: `/admin/catalog/need-mappings/${id}`,
    body,
  });
}

export function deleteNeedMapping(id: string): Promise<void> {
  // 204 hard delete — no response body (mirrors field-group delete; need
  // mappings are pure config, nothing references them).
  return apiClient.delete<void>(`/admin/catalog/need-mappings/${id}`);
}

export const needMappingKeys = {
  /** `["admin","need-mappings","list"]` — the one (un-filtered, un-paginated) list. */
  list: () => ["admin", "need-mappings", "list"] as const,
} as const;

/**
 * `field_type` -> the matcher kind it supports, or `null` if the field isn't
 * mappable at all (brief's rule, mirrors `service._validate_matcher`'s own
 * field-type gate): `size`/`number` -> `numeric_range`; `select` ->
 * `option_set`; every other type (`text`/`boolean`/`date`/`image`) has no
 * matcher shape and is excluded from the field picker entirely.
 */
export function matcherKindForFieldType(fieldType: string): NeedMappingMatcherKind | null {
  if (fieldType === "size" || fieldType === "number") return "numeric_range";
  if (fieldType === "select") return "option_set";
  return null;
}
